/**
 * Lightweight cross-module cache for the UCM user's configured line count.
 *
 * Populated by UcmLineService.getLines() after a successful UC Config REST fetch.
 * Read by WebexCallHistoryAdapter when computing `hasMultipleLines` for UCM users.
 *
 * Rationale: UCM has no Mercury push event for line data (unlike WXC's
 * integration.phoneservices), so the call history adapter cannot derive the line
 * count from localStorage on its own. Caching the count here keeps line-discovery
 * responsibility scoped to the LineSelector module; the call history adapter
 * simply reads the cached value.
 *
 * Storage: localStorage so the value survives reloads. Bounded to a small integer
 * range — no PII, no sensitive data.
 *
 * Ref: UCM_LINE_SELECTION_DESIGN.md §10 — Call History Bug Fix
 */

import { Logger } from '../../../packages/logger/src';

const UCM_LINE_COUNT_STORAGE_KEY = 'webex_ucmLineCount';

/**
 * Persist the configured UCM line count for the current user.
 * Negative inputs are coerced to 0; non-integers are floored.
 */
export function setUcmLineCount(count: number): void {
  const safeCount = Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
  try {
    localStorage.setItem(UCM_LINE_COUNT_STORAGE_KEY, String(safeCount));
    Logger.debug(`[LineSelection][UCM] Cached line count: ${safeCount}`);
  } catch (err) {
    Logger.error(`[LineSelection][UCM] Failed to cache line count: ${err}`);
  }
}

/**
 * Read the cached UCM line count. Returns 0 if missing or invalid.
 */
export function getUcmLineCount(): number {
  try {
    const stored = localStorage.getItem(UCM_LINE_COUNT_STORAGE_KEY);
    if (stored === null) return 0;
    const parsed = parseInt(stored, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch (err) {
    Logger.error(`[LineSelection][UCM] Failed to read cached line count: ${err}`);
    return 0;
  }
}

/**
 * Clear the cached UCM line count (e.g., on logout or backend change).
 */
export function clearUcmLineCount(): void {
  try {
    localStorage.removeItem(UCM_LINE_COUNT_STORAGE_KEY);
  } catch (err) {
    Logger.error(`[LineSelection][UCM] Failed to clear cached line count: ${err}`);
  }
}
